import React, { useState, useEffect } from "react";
import { message, Tag } from "antd";
import { EmploymentTypeEnumLabel } from "Constants/Enums/EmploymentTypeEnum";
import API from "api/endpoints";
import axiosInstance from "api/axiosInstance";
import jsPDF from "jspdf";
import "jspdf-autotable";

function SkillReportService() {
  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  // Point 5 (resourcing): optional availability date range sent to the API.
  const [availabilityFromDate, setAvailabilityFromDate] = useState(null);
  const [availabilityToDate, setAvailabilityToDate] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get(API.department.getList);
        setDepartments(response.data || []);
      } catch (error) {
        console.error("Error fetching departments:", error);
        message.error("Failed to load departments");
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Fetch skills based on department selection
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        let url = API.skills.getList || "/Skills/GetList";

        if (selectedDepartment) {
          url += url.includes("?")
            ? `&DepartmentId=${selectedDepartment}`
            : `?DepartmentId=${selectedDepartment}`;
        }

        const response = await axiosInstance.get(url);
        setSkills(response.data || []);

        // Clear selected skills when department changes
        setSelectedSkills([]);
        setFilteredData([]);
      } catch (error) {
        console.error("Error fetching skills:", error);
        message.error("Failed to load skills");
        setSkills([]);
      } finally {
        setSkillsLoading(false);
      }
    };

    fetchSkills();
  }, [selectedDepartment]);

  // Handle department change
  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartment(departmentId || "");
  };

  // Shared fetch: by skills + optional department + optional availability range.
  // availabilityFromDate/ToDate are the Point 5 resourcing fields; when omitted
  // the backend defaults to today.
  const fetchEmployeesBySkills = async (skillIds, departmentId, fromDate, toDate) => {
    if (!skillIds || skillIds.length === 0) {
      setFilteredData([]);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        SkillIds: skillIds,
        DepartmentId: departmentId || null,
      };
      if (fromDate) payload.availabilityFromDate = fromDate;
      if (toDate) payload.availabilityToDate = toDate;

      const response = await axiosInstance.post(API.reports.getEmployeesBySkills, payload);
      const employeeData = response.data || [];
      const transformedData = employeeData.map((employee) => ({
        id: employee.id,
        employeeId: employee.publicId,
        employeeName: employee.fullName,
        departmentName: employee.departmentName,
        skillsList: (employee.employeeSkills || []).map((s) => s.skillName).join(", "),
        jobTitleName: employee.jobTitleName || "N/A",
        hiringDate: employee.hiredDate,
        // Point 5 (resourcing) fields.
        employmentType: employee.employmentType,
        isAvailableForRequestedPeriod: employee.isAvailableForRequestedPeriod,
        hasApprovedLeaveOverlap: employee.hasApprovedLeaveOverlap,
        leaveFromDate: employee.leaveFromDate,
        leaveToDate: employee.leaveToDate,
        leaveType: employee.leaveType,
      }));

      setFilteredData(transformedData);

      if (transformedData.length === 0) {
        message.info("No employees found with the selected skills");
      } else {
        message.success(`Found ${transformedData.length} employees with selected skills`);
      }
    } catch (error) {
      console.error("Error fetching employees by skills:", error);
      message.error("Failed to load employees data");
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle skills selection (tags mode). Existing skills arrive as numeric IDs;
  // anything the user types arrives as a string -> create-or-get it in the global
  // catalog, add it to the options, and search with the resolved IDs.
  const handleSkillsChange = async (selectedValues) => {
    const values = selectedValues || [];
    const existingIds = values.filter((v) => typeof v === "number");
    const newNames = values.filter((v) => typeof v === "string" && v.trim());

    let createdIds = [];
    if (newNames.length > 0) {
      try {
        const created = await Promise.all(
          newNames.map((name) =>
            axiosInstance.post(API.skills.createOrGet, { name: name.trim() }).then((r) => r.data)
          )
        );
        setSkills((prev) => {
          const map = new Map((prev || []).map((s) => [s.id, s]));
          created.forEach((c) => {
            if (c && c.id != null) map.set(c.id, c);
          });
          return Array.from(map.values());
        });
        createdIds = created.map((c) => c?.id).filter((id) => id != null);
      } catch (error) {
        console.error("Error adding custom skill:", error);
        message.error("Failed to add the custom skill");
      }
    }

    const allIds = [...existingIds, ...createdIds];
    setSelectedSkills(allIds);
    fetchEmployeesBySkills(allIds, selectedDepartment, availabilityFromDate, availabilityToDate);
  };

  // Handle availability range change - re-run search if skills are selected.
  const handleDateRangeChange = (fromDate, toDate) => {
    setAvailabilityFromDate(fromDate);
    setAvailabilityToDate(toDate);
    if (selectedSkills.length > 0) {
      fetchEmployeesBySkills(selectedSkills, selectedDepartment, fromDate, toDate);
    }
  };

  // Helper function to get department name from ID
  const getDepartmentName = (departmentId) => {
    if (!departmentId) return "N/A";
    const department = departments.find((dept) => dept.id === departmentId);
    return department ? department.name : `Department ${departmentId}`;
  };

  // Download report as PDF
  const handleDownloadReport = () => {
    if (!filteredData.length) {
      message.warning("No data available to download");
      return;
    }

    try {
      const doc = new jsPDF("landscape");

      // Title
      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text("SKILLS REPORT", 148, 15, { align: "center" });

      // Report info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);

      let currentY = 25;

      // Department info
      const departmentName = selectedDepartment
        ? getDepartmentName(parseInt(selectedDepartment))
        : "All Departments";
      doc.text(`Department: ${departmentName}`, 14, currentY);
      currentY += 6;

      // Selected skills info
      const selectedSkillNames = selectedSkills
        .map((skillId) => {
          const skill = skills.find((s) => s.id === skillId);
          return skill ? skill.name : `Skill ${skillId}`;
        })
        .join(", ");

      doc.text(`Selected Skills: ${selectedSkillNames}`, 14, currentY);
      currentY += 6;

      // Date and record count
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, currentY);
      currentY += 6;

      doc.text(`Total Records: ${filteredData.length}`, 14, currentY);
      currentY += 15;

      // Table headers - ONLY 6 requested columns
      const headers = [
        [
          "Emp ID",
          "Name",
          "Department",
          "Skills List",
          "Job Title",
          "Employment Type",
          "Availability",
          "Hiring Date",
        ],
      ];

      // Table data - ONLY 6 requested columns
      const tableData = filteredData.map((employee) => {
        let availability = "N/A";
        if (employee.hasApprovedLeaveOverlap) {
          const range = [employee.leaveFromDate, employee.leaveToDate]
            .filter(Boolean)
            .map((d) => new Date(d).toLocaleDateString())
            .join(" - ");
          availability = ["On Leave", employee.leaveType, range].filter(Boolean).join(" - ");
        } else if (employee.isAvailableForRequestedPeriod) {
          availability = "Available";
        }

        const et = employee.employmentType;
        const employmentTypeLabel =
          et === null || et === undefined || et === ""
            ? "N/A"
            : EmploymentTypeEnumLabel[et] || (typeof et === "string" ? et : String(et));

        return [
          employee.employeeId?.toString() || "N/A",
          employee.employeeName || "N/A",
          employee.departmentName || "N/A",
          employee.skillsList || "No skills",
          employee.jobTitleName || "N/A",
          employmentTypeLabel,
          availability,
          employee.hiringDate ? new Date(employee.hiringDate).toLocaleDateString() : "N/A",
        ];
      });

      // Generate table
      doc.autoTable({
        startY: currentY,
        head: headers,
        body: tableData,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [70, 130, 180],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 22 }, // Emp ID
          1: { cellWidth: 26 }, // Name
          2: { cellWidth: 24 }, // Department
          3: { cellWidth: 60 }, // Skills List
          4: { cellWidth: 30 }, // Job Title
          5: { cellWidth: 26 }, // Employment Type
          6: { cellWidth: 42 }, // Availability
          7: { cellWidth: 24 }, // Hiring Date
        },
        margin: { left: 10, right: 10 },
      });

      // Generate filename
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `Skills-Report-${departmentName.replace(/\s+/g, "-")}-${timestamp}.pdf`;

      // Save the PDF
      doc.save(filename);
      message.success("PDF report downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error("Failed to generate PDF report");
    }
  };

  // Table columns - ONLY 6 requested columns
  const columns = [
    {
      title: "Emp ID",
      dataIndex: "employeeId",
      key: "employeeId",
      width: 100,
    },
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
      sorter: (a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""),
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
      sorter: (a, b) => (a.departmentName || "").localeCompare(b.departmentName || ""),
    },
    {
      title: "Skills List",
      dataIndex: "skillsList",
      key: "skillsList",
    },
    {
      title: "Job Title",
      dataIndex: "jobTitleName",
      key: "jobTitleName",
      sorter: (a, b) => (a.jobTitleName || "").localeCompare(b.jobTitleName || ""),
    },
    {
      title: "Employment Type",
      dataIndex: "employmentType",
      key: "employmentType",
      render: (v) => {
        if (v === null || v === undefined || v === "") return "N/A";
        // API may return employmentType as a numeric enum or the string label.
        return EmploymentTypeEnumLabel[v] || (typeof v === "string" ? v : String(v));
      },
    },
    {
      title: "Availability",
      key: "availability",
      render: (_, r) => {
        if (r.hasApprovedLeaveOverlap) {
          const range = [r.leaveFromDate, r.leaveToDate]
            .filter(Boolean)
            .map((d) => new Date(d).toLocaleDateString())
            .join(" - ");
          return (
            <div>
              <Tag color="orange">On Leave</Tag>
              <div style={{ fontSize: "0.78rem", color: "#888" }}>
                {[r.leaveType, range].filter(Boolean).join(" · ")}
              </div>
            </div>
          );
        }
        if (r.isAvailableForRequestedPeriod) {
          return <Tag color="green">Available</Tag>;
        }
        return "N/A";
      },
    },
    {
      title: "Hiring Date",
      dataIndex: "hiringDate",
      key: "hiringDate",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
      sorter: (a, b) => new Date(a.hiringDate || 0) - new Date(b.hiringDate || 0),
    },
  ];

  return {
    filteredData: filteredData || [],
    selectedSkills: selectedSkills || [],
    selectedDepartment: selectedDepartment || "",
    availabilityFromDate,
    availabilityToDate,
    skills: skills || [],
    departments: departments || [],
    columns,
    loading: loading || skillsLoading,
    handleSkillsChange,
    handleDepartmentChange,
    handleDateRangeChange,
    handleDownloadReport,
  };
}

export default SkillReportService;
